import { ProductSelectDto } from '@@types/api.types';
import { Modal } from '@components/core/modal.component';
import { ConfirmDeleteModal } from '@components/shared/confirm-delete-modal.component';
import { EmptyStateContainer } from '@components/shared/empty-state-container.component';
import { ModalSteps, useSteps } from '@components/shared/modal-steps.component';
import { Checkbox } from '@components/ui/checkbox';
import { useAppForm } from '@components/ui/form/form';
import { errorToast } from '@hooks/toast.hook';
import {
  useInnovatorCreateProduct,
  useInnovatorDeleteProduct,
  useInnovatorUpdateProduct,
} from '@queries/product.query';
import { useTechnologiesQueryOptions } from '@queries/technology.query';
import { ProductFormData, productSchema } from '@schemas/product.schema';
import { useQuery } from '@tanstack/react-query';
import { FunctionComponent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { routes } from '../../../../routes';
import { LoadingStateContainer } from '../../../shared/loading-state-container.component';

type ProductFormModalProps = {
  defaultStep?: Steps;
  isEdit?: boolean;
  isOpen: boolean;
  onClose: () => void;
  onDelete?: () => void;
  product?: ProductSelectDto;
};

export enum Steps {
  DETAILS = 0,
  TECHNOLOGIES = 1,
}

const stepData = [
  {
    step: Steps.DETAILS,
    label: 'Details',
    guidance: '',
  },
  {
    step: Steps.TECHNOLOGIES,
    label: 'Technologies',
    guidance: '',
  },
];

const detailsSchema = productSchema.pick({ name: true, description: true });
const technologiesSchema = productSchema.pick({ technologyIds: true });

type DetailsFormData = z.infer<typeof detailsSchema>;
type TechnologiesFormData = z.infer<typeof technologiesSchema>;

export const ProductFormModal: FunctionComponent<ProductFormModalProps> = (props) => {
  const { defaultStep = Steps.DETAILS, isEdit = false, isOpen, onClose, onDelete, product } = props;
  const navigate = useNavigate();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { step, stepsInfo, handleStepClick, resetSteps, updateStepState } = useSteps(stepData, defaultStep, isEdit);

  const { mutateAsync: createProduct } = useInnovatorCreateProduct();
  const { mutateAsync: updateProduct } = useInnovatorUpdateProduct();
  const { mutateAsync: deleteProduct, isPending: isDeletingProduct } = useInnovatorDeleteProduct();
  const technologiesQueryOptions = useTechnologiesQueryOptions();
  const { data: technologies, isPending: isTechnologiesPending } = useQuery(technologiesQueryOptions);

  const detailsDefaultValues: DetailsFormData = {
    name: product?.name || '',
    description: product?.description || '',
  };

  const technologiesDefaultValues: TechnologiesFormData = {
    technologyIds: product?.technologies?.map((tech) => tech.id) || [],
  };

  const detailsForm = useAppForm({
    defaultValues: detailsDefaultValues,
    validators: {
      onSubmit: detailsSchema,
    },
  });

  const technologiesForm = useAppForm({
    defaultValues: technologiesDefaultValues,
    validators: {
      onSubmit: technologiesSchema,
    },
  });

  const handleClose = () => {
    detailsForm.reset();
    technologiesForm.reset();
    resetSteps();
    onClose();
  };

  const handleDelete = async () => {
    if (!isEdit || !product) return;

    try {
      await deleteProduct({ id: product.id });
      handleClose();
      onDelete?.();
    } catch (error) {
      console.error('Error deleting product:', error);
      errorToast({
        description: 'Failed to delete product.',
      });
    }
  };

  const validateCurrentStep = async () => {
    if (step === Steps.DETAILS) {
      const errors = await detailsForm.validateAllFields('submit');
      return errors.length === 0;
    }

    if (step === Steps.TECHNOLOGIES) {
      const errors = await technologiesForm.validateAllFields('submit');
      return errors.length === 0;
    }

    return false;
  };

  const handleSubmit = async () => {
    try {
      const detailsData = detailsForm.state.values;
      const technologiesData = technologiesForm.state.values;

      const formData: ProductFormData = {
        ...detailsData,
        ...technologiesData,
      };

      if (isEdit && product) {
        await updateProduct({
          id: product.id,
          ...formData,
        });
      } else {
        await createProduct(formData);
      }
      handleClose();
    } catch (error) {
      console.error('Error saving product:', error);
      errorToast({
        description: `Failed to ${isEdit ? 'update' : 'create'} product.`,
      });
    }
  };

  const handleNext = async () => {
    const isValid = await validateCurrentStep();
    if (!isValid) return;

    if (isEdit) {
      await handleSubmit();
    } else if (step < Steps.TECHNOLOGIES) {
      updateStepState(step);
    } else {
      await handleSubmit();
    }
  };

  const handleStepClickWithValidation = async (targetStep: Steps) => {
    if (targetStep === step) return;

    const isValid = await validateCurrentStep();

    if (isValid) {
      handleStepClick(targetStep);
    }
  };

  return (
    <Modal
      footer={{
        leftButton: isEdit
          ? {
              disabled: isDeletingProduct,
              onClick: () => setIsDeleteModalOpen(true),
              children: 'Delete',
              variant: 'destructive' as const,
            }
          : {
              onClick: handleClose,
              children: 'Cancel',
            },
        rightButton: {
          loading: detailsForm.state.isSubmitting || technologiesForm.state.isSubmitting,
          onClick: handleNext,
          children: isEdit ? 'Update Product' : step === Steps.TECHNOLOGIES ? 'Create Product' : 'Continue',
        },
      }}
      header={{
        title: isEdit ? 'Update Product' : 'Create Product',
      }}
      isOpen={isOpen}
      onClose={handleClose}
      width="500px"
    >
      {isEdit && (
        <ConfirmDeleteModal
          isOpen={isDeleteModalOpen}
          itemName={product?.name || 'product'}
          onConfirm={handleDelete}
          setIsOpen={setIsDeleteModalOpen}
        />
      )}

      <div className="flex flex-col gap-4 py-2">
        <ModalSteps
          stepsInfo={stepsInfo}
          onStepClick={handleStepClickWithValidation}
        />
      </div>

      <div className="space-y-4">
        {step === Steps.DETAILS && (
          <detailsForm.AppForm>
            <section className="space-y-4">
              <detailsForm.AppField
                name="name"
                validators={{
                  onBlur: detailsSchema.shape.name,
                }}
              >
                {(field) => (
                  <field.TextField
                    autoComplete="off"
                    data-1p-ignore
                    label="Name"
                    placeholder="Enter product name"
                    required
                  />
                )}
              </detailsForm.AppField>
              <detailsForm.AppField
                name="description"
                validators={{
                  onBlur: detailsSchema.shape.description,
                }}
              >
                {(field) => (
                  <field.TextAreaField
                    label="Description"
                    placeholder="Enter product description"
                    rows={5}
                  />
                )}
              </detailsForm.AppField>
            </section>
          </detailsForm.AppForm>
        )}

        {step === Steps.TECHNOLOGIES && (
          <technologiesForm.AppForm>
            <section className="space-y-4">
              <technologiesForm.AppField
                mode="array"
                name="technologyIds"
              >
                {(field) => (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {isTechnologiesPending && (
                      <LoadingStateContainer
                        type="skeleton"
                        contentLines={6}
                      />
                    )}
                    {!isTechnologiesPending && technologies?.length === 0 && (
                      <EmptyStateContainer
                        className="min-h-[200px] text-center"
                        title="No Technologies"
                        message="Add Technology"
                        onClick={() => {
                          navigate(routes.technologies.path);
                        }}
                      />
                    )}
                    {technologies?.map((technology) => {
                      const fieldValue = field.state.value || [];
                      const isSelected = fieldValue.includes(technology.id);
                      return (
                        <label
                          className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                          key={technology.id}
                        >
                          <Checkbox
                            checked={isSelected}
                            className="size-5"
                            onCheckedChange={(checked) => {
                              if (checked) {
                                if (!isSelected) {
                                  field.pushValue(technology.id);
                                }
                              } else {
                                const index = fieldValue.indexOf(technology.id);
                                if (index > -1) {
                                  field.removeValue(index);
                                }
                              }
                            }}
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{technology.name}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </technologiesForm.AppField>
            </section>
          </technologiesForm.AppForm>
        )}
      </div>
    </Modal>
  );
};
